/**
 * LoopOffice — new Office subsystems wired to the Loop / task_events backend.
 *
 * Three composable pieces:
 *   · <AgentPulseBar />     — live state of Aria · Lyra · Hermes (top band)
 *   · <LoopKanban />        — 7-column board (backlog → briefing → thinking →
 *                              consulting → executing → review → done),
 *                              cards color-coded by agent + click → timeline
 *   · <LiveActivityStream/> — right column · typed events (real-time)
 *
 * All three share a single 6s polling cadence against:
 *   GET /api/projects/{id}/loop/pulse
 *   GET /api/projects/{id}/loop/stream
 *   GET /api/monetize/projects/{id}/hq            (existing endpoint for tasks)
 *
 * Design coherent with Workshop:
 *   · 12-16px border-radius
 *   · per-agent gradients (violet/orange/amber)
 *   · pulse animation: 1.5s ease-in-out, opacity 0.6→1.0
 *   · all data-testid'd for QA
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Activity, Clock, MessageCircle, Wrench, CheckCircle2, AlertOctagon, Pause, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AGENT_META = {
  aria:   { name: 'Aria',   role: 'The Architect',  accent: '#7C3AED', soft: '#A78BFA', gradient: 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)' },
  lyra:   { name: 'Lyra',   role: 'The Storyteller', accent: '#FF6B1A', soft: '#FFD166', gradient: 'linear-gradient(135deg, #FFD166 0%, #FF6B1A 100%)' },
  hermes: { name: 'Hermes', role: 'The Pathfinder',  accent: '#FFB347', soft: '#FFE89D', gradient: 'linear-gradient(135deg, #FFE89D 0%, #FFB347 100%)' },
  // Legacy collapse aliases (graceful migration).
  builder: { name: 'Aria', role: 'The Architect', accent: '#7C3AED', soft: '#A78BFA', gradient: 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)' },
  markus: { name: 'Hermes', role: 'The Pathfinder', accent: '#FFB347', soft: '#FFE89D', gradient: 'linear-gradient(135deg, #FFE89D 0%, #FFB347 100%)' },
  marcus: { name: 'Hermes', role: 'The Pathfinder', accent: '#FFB347', soft: '#FFE89D', gradient: 'linear-gradient(135deg, #FFE89D 0%, #FFB347 100%)' },
};

const STATE_META = {
  standby:    { label: 'Standby',    color: '#9CA3AF', pulse: false, dot: '◯' },
  briefing:   { label: 'Briefing',   color: '#6366F1', pulse: true,  dot: '●' },
  thinking:   { label: 'Thinking',   color: '#8B5CF6', pulse: true,  dot: '●' },
  consulting: { label: 'Consulting', color: '#06B6D4', pulse: true,  dot: '●' },
  executing:  { label: 'Executing',  color: '#10B981', pulse: true,  dot: '●' },
  review:     { label: 'Review',     color: '#F59E0B', pulse: false, dot: '◆' },
  blocked:    { label: 'Blocked',    color: '#EF4444', pulse: true,  dot: '⏸' },
  delivered:  { label: 'Delivered',  color: '#10B981', pulse: false, dot: '✓' },
};

const KANBAN_COLUMNS = [
  { key: 'backlog',    label: 'Backlog',    icon: Clock,         color: '#9CA3AF' },
  { key: 'briefing',   label: 'Briefing',   icon: MessageCircle, color: '#6366F1' },
  { key: 'thinking',   label: 'Thinking',   icon: Sparkles,      color: '#8B5CF6' },
  { key: 'consulting', label: 'Consulting', icon: MessageCircle, color: '#06B6D4' },
  { key: 'executing',  label: 'Executing',  icon: Wrench,        color: '#10B981' },
  { key: 'review',     label: 'Review',     icon: AlertOctagon,  color: '#F59E0B' },
  { key: 'done',       label: 'Done',       icon: CheckCircle2,  color: '#0EA5E9' },
];

const EVENT_ICON = {
  created:    Sparkles,
  claimed:    MessageCircle,
  thinking:   Sparkles,
  consulting: MessageCircle,
  executing:  Wrench,
  delivered:  CheckCircle2,
  blocked:    Pause,
  validated:  CheckCircle2,
  cancelled:  AlertOctagon,
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

// Normalize owner_agent_id (legacy aliases → trinity)
const normalizeOwner = (o) => {
  const x = String(o || '').toLowerCase();
  if (['markus', 'marcus', 'revenue', 'sales', 'growth'].includes(x)) return 'hermes';
  if (['builder', 'aria'].includes(x)) return 'aria';
  if (['content', 'lyra'].includes(x)) return 'lyra';
  if (['hermes'].includes(x)) return 'hermes';
  return 'aria';
};

/* ============================== PULSE BAR ============================== */

export function AgentPulseBar({ projectId }) {
  const [pulse, setPulse] = useState({
    aria:   { state: 'standby' },
    lyra:   { state: 'standby' },
    hermes: { state: 'standby' },
  });

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    const fetchPulse = async () => {
      try {
        const r = await fetch(`${API}/api/projects/${projectId}/loop/pulse`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setPulse(data);
      } catch { /* silent retry next tick */ }
    };
    fetchPulse();
    const t = setInterval(fetchPulse, 6000);
    return () => { cancelled = true; clearInterval(t); };
  }, [projectId]);

  return (
    <div data-testid="agent-pulse-bar" style={{
      padding: '14px 28px',
      borderBottom: '1px solid rgba(15,15,30,0.06)',
      background: 'linear-gradient(180deg, #FBFAF7 0%, #F4F2EC 100%)',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 12,
    }}>
      {['aria', 'lyra', 'hermes'].map(key => {
        const meta = AGENT_META[key];
        const p = pulse[key] || { state: 'standby' };
        const sm = STATE_META[p.state] || STATE_META.standby;
        return (
          <div key={key} data-testid={`pulse-${key}`} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 14px',
            borderRadius: 12,
            background: '#FFFFFF',
            border: `1px solid ${meta.soft}33`,
            boxShadow: '0 1px 3px rgba(15,15,30,0.04)',
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: sm.color,
              animation: sm.pulse ? 'pulseDot 1.5s ease-in-out infinite' : 'none',
              flexShrink: 0,
            }} />
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: 11, fontWeight: 800, letterSpacing: '0.18em',
                  textTransform: 'uppercase', color: meta.accent,
                }}>
                  {meta.name}
                </span>
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.14em',
                  textTransform: 'uppercase', color: sm.color,
                }}>
                  {sm.label}
                </span>
              </div>
              <div style={{
                fontSize: 12, color: '#4B4B5C', marginTop: 2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {p.summary || (p.state === 'standby' ? 'Idle · waiting for next atom' : '')}
              </div>
            </div>
            {p.since && (
              <span style={{ fontSize: 10, color: '#9CA3AF', flexShrink: 0 }}>
                {fmtTime(p.since)}
              </span>
            )}
          </div>
        );
      })}
      <style>{`
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
      `}</style>
    </div>
  );
}

/* ============================== KANBAN (7 columns) ============================== */

export function LoopKanban({ projectId, tasks, onTaskClick, onReviewTask }) {
  const [timelineTask, setTimelineTask] = useState(null);
  const grouped = useMemo(() => {
    const out = {};
    KANBAN_COLUMNS.forEach(c => { out[c.key] = []; });
    for (const t of tasks || []) {
      // Map legacy status → new current_state
      let st = t.current_state;
      if (!st) {
        const legacy = t.status || 'backlog';
        st = ({ backlog: 'backlog', in_progress: 'executing', blocked: 'blocked', done: 'done' })[legacy] || 'backlog';
      }
      if (!out[st]) st = 'backlog';
      out[st].push(t);
    }
    return out;
  }, [tasks]);

  return (
    <div data-testid="loop-kanban" style={{
      flex: 1, minHeight: 0,
      display: 'flex', flexDirection: 'column',
      background: '#FFFFFF',
      border: '1px solid rgba(15,15,30,0.06)',
      borderRadius: 16,
      margin: '20px 28px 8px 28px',
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '14px 18px',
        borderBottom: '1px solid rgba(15,15,30,0.06)',
        background: '#FBFAF7',
      }}>
        <Activity size={14} color="#7C3AED" />
        <span style={{
          fontSize: 11, fontWeight: 800, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: '#0F0F1E',
        }}>
          Today&apos;s Board · {tasks?.length || 0} atoms
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: '#6B7280' }}>
          drag · or ship from The Loop
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${KANBAN_COLUMNS.length}, minmax(180px, 1fr))`,
        gap: 0,
        flex: 1, minHeight: 0,
        overflowX: 'auto',
      }}>
        {KANBAN_COLUMNS.map(col => {
          const Icon = col.icon;
          const items = grouped[col.key] || [];
          return (
            <div key={col.key} data-testid={`kanban-col-${col.key}`} style={{
              borderRight: '1px solid rgba(15,15,30,0.05)',
              display: 'flex', flexDirection: 'column',
              minHeight: 360,
            }}>
              <div style={{
                padding: '10px 12px',
                borderBottom: '1px solid rgba(15,15,30,0.04)',
                background: `${col.color}08`,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <Icon size={12} color={col.color} />
                <span style={{
                  fontSize: 10, fontWeight: 800, letterSpacing: '0.2em',
                  textTransform: 'uppercase', color: col.color,
                }}>
                  {col.label}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{
                  fontSize: 10, fontWeight: 700, color: col.color,
                  background: `${col.color}15`,
                  padding: '2px 8px', borderRadius: 8,
                }}>
                  {items.length}
                </span>
              </div>
              <div style={{
                flex: 1, padding: '8px 8px 16px',
                display: 'flex', flexDirection: 'column', gap: 8,
                overflowY: 'auto',
              }}>
                {items.length === 0 && (
                  <div style={{
                    padding: 14, textAlign: 'center',
                    fontSize: 11, color: '#9CA3AF', fontStyle: 'italic',
                  }}>empty</div>
                )}
                {items.map(t => (
                  <KanbanCard
                    key={t.task_id}
                    task={t}
                    columnKey={col.key}
                    onClick={() => {
                      if (t.current_state === 'review' && onReviewTask) {
                        onReviewTask(t);
                      } else if (onTaskClick) {
                        onTaskClick(t);
                      } else {
                        // Default behaviour: pop the embedded timeline drawer.
                        setTimelineTask(t);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {timelineTask && (
        <TaskTimelineDrawer
          task={timelineTask}
          onClose={() => setTimelineTask(null)}
        />
      )}
    </div>
  );
}

/* ============================== TASK TIMELINE DRAWER ============================== */
/**
 * Right-side slide-over showing every task_event ever logged on a task —
 * `claimed`, `consulted`, `delivered`, `validated`, etc. Pulls from the
 * existing `GET /api/tasks/{task_id}/timeline` endpoint, no new backend
 * code needed. Color-coded by emitting agent. Polls every 6s so the founder
 * can watch an agent's reasoning unfold without refreshing.
 */
function TaskTimelineDrawer({ task, onClose }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    if (!task?.task_id) return;
    let live = true;
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/tasks/${task.task_id}/timeline`, { credentials: 'include' });
        if (!r.ok) { if (live) setEvents([]); return; }
        const d = await r.json();
        if (live) setEvents(d.events || []);
      } catch { if (live) setEvents([]); }
    };
    load();
    const t = setInterval(load, 6000);
    return () => { live = false; clearInterval(t); };
  }, [task?.task_id]);

  const owner = normalizeOwner(task.owner_agent_id);
  const meta = AGENT_META[owner];

  return (
    <div
      data-testid="task-timeline-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,15,30,0.32)',
        backdropFilter: 'blur(4px)', zIndex: 60,
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <aside
        data-testid="task-timeline-drawer"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(480px, 92vw)', height: '100%',
          background: '#FFFFFF',
          boxShadow: '-12px 0 32px rgba(15,15,30,0.18)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          padding: '16px 18px', borderBottom: '1px solid rgba(15,15,30,0.06)',
          background: '#FBFAF7',
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: meta.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, color: '#FFFFFF', fontWeight: 800, fontSize: 11,
          }}>{meta.name.slice(0, 2).toUpperCase()}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 9, fontWeight: 800, letterSpacing: '0.18em',
              color: meta.accent, textTransform: 'uppercase', marginBottom: 4,
            }}>
              {meta.name} · {task.current_state || 'backlog'}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0F0F1A', lineHeight: 1.35 }}>
              {task.title}
            </div>
          </div>
          <button
            data-testid="task-timeline-close"
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#6B7280', padding: 4, lineHeight: 0, fontSize: 22,
            }}
            title="Close"
          >×</button>
        </div>

        <div style={{
          flex: 1, overflowY: 'auto', padding: '14px 18px 24px',
          background: '#FFFFFF',
        }}>
          {events === null && (
            <div style={{ color: '#9CA3AF', fontSize: 12, fontStyle: 'italic' }}>
              Loading timeline…
            </div>
          )}
          {events && events.length === 0 && (
            <div style={{ color: '#9CA3AF', fontSize: 12, fontStyle: 'italic' }}>
              No events yet for this task.
            </div>
          )}
          {events && events.length > 0 && (
            <ol style={{
              listStyle: 'none', margin: 0, padding: 0,
              borderLeft: '1px solid rgba(15,15,30,0.08)', paddingLeft: 14,
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              {events.map((ev, i) => {
                const ag = normalizeOwner(ev.agent_id);
                const am = AGENT_META[ag];
                return (
                  <li
                    key={ev.event_id || i}
                    data-testid={`timeline-event-${i}`}
                    style={{ position: 'relative', paddingLeft: 4 }}
                  >
                    <span style={{
                      position: 'absolute', left: -19, top: 5,
                      width: 10, height: 10, borderRadius: '50%',
                      background: am.gradient,
                      boxShadow: `0 0 8px ${am.soft}80`,
                    }} />
                    <div style={{ display: 'flex', gap: 6, marginBottom: 2, alignItems: 'baseline' }}>
                      <span style={{
                        fontSize: 9.5, fontWeight: 800, letterSpacing: '0.14em',
                        color: am.accent, textTransform: 'uppercase',
                      }}>{am.name}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
                        color: '#6B7280', textTransform: 'uppercase',
                      }}>· {ev.kind}</span>
                      <span style={{ flex: 1 }} />
                      <span style={{ fontSize: 10, color: '#9CA3AF' }}>{fmtTime(ev.created_at)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#0F0F1A', lineHeight: 1.5 }}>
                      {ev.summary || '(no summary)'}
                    </div>
                    {ev.detail && (typeof ev.detail === 'object') && Object.keys(ev.detail).length > 0 && (
                      <pre style={{
                        marginTop: 6, padding: 8, background: '#FBFAF7',
                        borderRadius: 6, fontSize: 10, color: '#4B4B5C',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        fontFamily: 'ui-monospace, monospace',
                        maxHeight: 100, overflowY: 'auto',
                      }}>{JSON.stringify(ev.detail, null, 2).slice(0, 400)}</pre>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </aside>
    </div>
  );
}

function KanbanCard({ task, columnKey, onClick }) {
  const owner = normalizeOwner(task.owner_agent_id);
  const meta = AGENT_META[owner];
  const priColor = task.priority === 'P0' ? '#EF4444' : task.priority === 'P1' ? '#F59E0B' : '#9CA3AF';
  const isReview = columnKey === 'review';
  // "From chat" — atoms born from Aria/Lyra/Hermes calling propose_loop_atom
  // in the chat (or per-recipient children Hermes spawns). The founder spots
  // them instantly so they know which work was triggered by the conversation
  // vs the strategic seed plan.
  const isFromChat = !!task.parent_task_id || task.origin === 'aria_proposal';
  return (
    <div
      data-testid={`kanban-card-${task.task_id}`}
      onClick={onClick}
      style={{
        padding: '10px 12px',
        background: isReview ? '#FFFBEB' : '#FFFFFF',
        border: `1px solid ${isReview ? '#FCD34D' : meta.soft + '40'}`,
        borderLeft: `3px solid ${meta.accent}`,
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'transform 0.15s, box-shadow 0.15s',
        position: 'relative',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(15,15,30,0.08)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.12em',
          color: priColor, padding: '1px 5px', borderRadius: 4,
          background: `${priColor}15`,
        }}>{task.priority || 'P1'}</span>
        {task.irreversible && (
          <span title="Irreversible action — requires founder validation"
            style={{ fontSize: 10, color: '#EF4444' }}>⚠</span>
        )}
        {isFromChat && (
          <span data-testid="kanban-from-chat-badge" title="Born from a chat with Aria · Lyra · Hermes" style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: 8, fontWeight: 800, letterSpacing: '0.1em',
            padding: '1px 6px', borderRadius: 4,
            background: 'linear-gradient(135deg, #EDE9FE 0%, #FCE7F3 100%)',
            color: '#7C3AED',
            border: '1px solid #C4B5FD',
          }}>
            <MessageCircle size={8} /> CHAT
          </span>
        )}
        <span style={{ flex: 1 }} />
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
          color: meta.accent, textTransform: 'uppercase',
        }}>{meta.name.slice(0, 2)}</span>
      </div>
      <div style={{
        fontSize: 12, fontWeight: 600, color: '#0F0F1E', lineHeight: 1.35,
        marginBottom: 6,
      }}>
        {task.title}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 10, color: '#9CA3AF', flex: 1 }}>
          {task.eta_minutes || 30}min · {fmtTime(task.last_event_at || task.created_at)}
        </span>
        {isReview && (
          <span style={{
            fontSize: 9, fontWeight: 800, color: '#92400E',
            background: '#FEF3C7', border: '1px solid #FCD34D',
            padding: '3px 8px', borderRadius: 999,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            Review →
          </span>
        )}
      </div>
    </div>
  );
}

/* ============================== LIVE ACTIVITY STREAM ============================== */

export function LiveActivityStream({ projectId, onTaskClick }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    const fetchStream = async () => {
      try {
        const r = await fetch(`${API}/api/projects/${projectId}/loop/stream?limit=40`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) {
          setEvents(data.events || []);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStream();
    const t = setInterval(fetchStream, 6000);
    return () => { cancelled = true; clearInterval(t); };
  }, [projectId]);

  return (
    <div data-testid="live-activity-stream" style={{
      width: 360, flexShrink: 0,
      background: '#FFFFFF',
      border: '1px solid rgba(15,15,30,0.06)',
      borderRadius: 16,
      margin: '20px 28px 8px 0',
      overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '14px 18px',
        borderBottom: '1px solid rgba(15,15,30,0.06)',
        background: '#FBFAF7',
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: '#10B981',
          animation: 'pulseDot 1.5s ease-in-out infinite',
        }} />
        <span style={{
          fontSize: 11, fontWeight: 800, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: '#0F0F1E',
        }}>
          Live Stream
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: '#9CA3AF' }}>
          {events.length} events
        </span>
      </div>

      <div style={{
        flex: 1, overflowY: 'auto', padding: '10px 14px',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {loading && <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic', padding: 14 }}>Loading the stream…</div>}
        {!loading && events.length === 0 && (
          <div style={{
            padding: '20px 14px', textAlign: 'center',
            fontSize: 12, color: '#9CA3AF', fontStyle: 'italic',
          }}>
            The team is silent. Compose The Loop to start the day&apos;s work.
          </div>
        )}
        {events.map(ev => {
          const Icon = EVENT_ICON[ev.kind] || Sparkles;
          const agent = normalizeOwner(ev.agent_id);
          const meta = AGENT_META[agent];
          const consultedMeta = ev.consulted_agent_id ? AGENT_META[normalizeOwner(ev.consulted_agent_id)] : null;
          return (
            <div
              key={ev.event_id}
              data-testid={`stream-event-${ev.event_id}`}
              onClick={() => onTaskClick?.(ev.task_id)}
              style={{
                display: 'flex', gap: 10, padding: '8px 10px',
                borderRadius: 8, cursor: 'pointer',
                background: '#FAFAFA',
                border: `1px solid ${meta.soft}26`,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#F4F2EC'}
              onMouseLeave={e => e.currentTarget.style.background = '#FAFAFA'}
            >
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                background: meta.gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon size={11} color="#FFFFFF" strokeWidth={2.5} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 800, letterSpacing: '0.14em',
                    color: meta.accent, textTransform: 'uppercase',
                  }}>{meta.name}</span>
                  {consultedMeta && (
                    <>
                      <span style={{ fontSize: 10, color: '#9CA3AF' }}>↔</span>
                      <span style={{
                        fontSize: 10, fontWeight: 800, letterSpacing: '0.14em',
                        color: consultedMeta.accent, textTransform: 'uppercase',
                      }}>{consultedMeta.name}</span>
                    </>
                  )}
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 9, color: '#9CA3AF' }}>
                    {fmtTime(ev.created_at)}
                  </span>
                </div>
                <div style={{
                  fontSize: 11, color: '#4B4B5C', lineHeight: 1.4,
                  overflow: 'hidden', display: '-webkit-box',
                  WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>
                  {ev.summary}
                </div>
                {(ev.duration_ms || ev.cost_usd) && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 9, color: '#9CA3AF' }}>
                    {ev.duration_ms && <span>{(ev.duration_ms / 1000).toFixed(2)}s</span>}
                    {ev.cost_usd && <span>· ${ev.cost_usd.toFixed(3)}</span>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
